import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';

export interface TableBankingLoan {
  id: number;
  member_id: number;
  table_banking_id: number;
  group_id: number;
  repayment_date: string;
  principal_amount: number;
  reason: string;
  status: string;
  loan_balance: number;
  total_amount: number;
  // Approval workflow fields
  approved_by?: number;
  approved_at?: string;
  approval_notes?: string;
  member?: { id: number; name: string };
  // Approval workflow user relationships
  appliedByUser?: { id: number; name: string };
  approvedByUser?: { id: number; name: string };
  disbursedByUser?: { id: number; name: string };
  repayments?: Array<{
    id: number;
    amount: number;
    payment_date: string;
    payment_method: string;
    description: string;
  }>;
  repayments_count?: number;
  total_repaid?: number;
  last_repayment_date?: string;
}

export interface TableBankingLoanFilters {
  search?: string;
  member_id?: number;
  status?: string;
  repayment_date?: string;
  has_repayments?: string;
  principal_min?: number;
  principal_max?: number;
  // Approval workflow filters
  approval_status?: string;
  applied_date_from?: string;
  applied_date_to?: string;
  approved_date_from?: string;
  approved_date_to?: string;
  approved_by?: number;
  page?: number;
  per_page?: number;
}

export const useTableBankingLoans = (filters: TableBankingLoanFilters = {}) =>
  useQuery({
    queryKey: ['tableBankingLoans', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await api.get(`/tablebanking?${params.toString()}`);
      return response.data;
    },
    staleTime: 30000, // 30 seconds
  });

export const useExportTableBankingLoans = () =>
  useMutation({
    mutationFn: async ({
      filters = {},
      format = 'excel',
    }: {
      filters?: TableBankingLoanFilters;
      format?: 'excel' | 'pdf' | 'csv';
    }) => {
      const params = new URLSearchParams();
      params.append('type', format);
      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'page' || key === 'per_page') {
          return;
        }
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/tablebanking/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const extension = format === 'pdf' ? 'pdf' : format === 'csv' ? 'csv' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `table-banking-loans-${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      toast.success('Export downloaded');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });


export const useCreateTableBankingLoan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (loan: Omit<TableBankingLoan, 'id'> | FormData) => {
      const config = loan instanceof FormData ? {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      } : {};
      
      const { data } = await api.post('/tablebanking', loan, config);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tableBankingLoans'] });
      // queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      // queryClient.invalidateQueries({ queryKey: ['loan-approval-dashboard-stats'] });
      toast.success('Loan created successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useUpdateTableBankingLoan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, loan }: { id: number; loan: FormData | any }) => {
      if (loan instanceof FormData) {
        // Add _method=PUT for Laravel to recognize this as a PUT request
        loan.append('_method', 'PUT');
        
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        };
        
        // Use POST with _method=PUT for file uploads
        const { data } = await api.post(`/tablebanking/${id}`, loan, config);
        return data;
      } else {
        // Use regular PUT for non-FormData updates
        const { data } = await api.put(`/tablebanking/${id}`, loan);
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tableBankingLoans'] });
      queryClient.invalidateQueries({ queryKey: ['member-table-banking-loan'] });
      // queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      // queryClient.invalidateQueries({ queryKey: ['loan-approval-dashboard-stats'] });
      toast.success('Loan updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useTableBankingLoan = (id: number) => {
  return useQuery({
    queryKey: ['member-table-banking-loan', id],
    queryFn: async () => {
      const { data } = await api.get(`/tablebanking/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

export const useDeleteTableBankingLoan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/tablebanking/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tableBankingLoans'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      toast.success('Loan deleted successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
}; 

//loan statistics
// export function useMemberLoanStatistics(filters = {}) {
//   return useQuery({
//     queryKey: ['member-loan-statistics', filters],
//     queryFn: async () => {
//       const params = new URLSearchParams();
      
//       // Add filters to query params
//       Object.entries(filters).forEach(([key, value]) => {
//         if (value !== undefined && value !== null && value !== '') {
//           params.append(key, value.toString());
//         }
//       });

//       const response = await api.get(`/memberloan/statistics?${params.toString()}`);
//       return response.data;
//     },
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     gcTime: 10 * 60 * 1000, // 10 minutes
//   });
// } 